/**
 * Shared clipboard helpers for the Office add-in handler modules.
 */

// Narrow whitelist of formatting tags that mark a string as real HTML. Needed
// because the tag-stripping regex below is destructive on plain prose: "ship it
// if x < y and z > 3" contains a run matching `<[^>]+>` exactly, and blind
// stripping would eat the middle of the sentence. Mirrors the backend's
// `_HTML_MARKUP_RE` in shared/tools/followup_pills.py.
const HTML_MARKUP_RE = /<\/?(p|ul|ol|li|strong|em|b|i|u|br|div|span|h[1-6]|table|tr|td|th|a|hr)\b/i;

/**
 * Render a possibly-HTML string as the plain text that should reach the clipboard.
 *
 * The Outlook copy path writes `text/plain` only, so anything with markup in it
 * gets pasted as literal tags. Email draft bodies are HTML by construction
 * (`<p>` per paragraph, `<br>` for line breaks — see the skill prompts), so
 * every Copy button on a draft needs this.
 *
 * Structure is preserved rather than collapsed: `<br>` becomes a newline, a
 * closing block tag becomes a blank line, and `<li>` keeps a bullet, because the
 * paste target is usually another email where losing the paragraph breaks is
 * nearly as bad as pasting the tags.
 *
 * Paragraph spacing is intentionally wider than ApprovalCard's `htmlToText`
 * (blank line vs single newline). Both read the same source string so the
 * content is identical; the card is a compact preview, this lands in an email
 * body where paragraphs want separating.
 *
 * Strings with no recognisable markup pass through untouched.
 */
export function htmlToClipboardText(value: string): string {
  if (!HTML_MARKUP_RE.test(value)) {
    return value.trim();
  }
  const doc = value
    .replace(/<br\s*\/?>/gi, "\n")
    // The opening <li> supplies the newline, so </li> adds nothing — otherwise
    // every list item would be separated by a blank line.
    .replace(/<li\b[^>]*>/gi, "\n• ")
    .replace(/<\/li\s*>/gi, "")
    // ul/ol close the break that </li> no longer supplies — without them the
    // last list item runs straight into the next paragraph.
    .replace(/<\/(p|div|tr|ul|ol|h[1-6])\s*>/gi, "\n\n")
    // Atomic-group emulation, not the obvious /<[^>]+>/g. That form backtracks
    // one character at a time on every unterminated "<" — super-linear on input
    // like "<" + "a".repeat(n) (Sonar typescript:S8786). Capturing inside a
    // lookahead and replaying it with \1 commits the match, so a "<" with no
    // closing ">" fails at that position immediately.
    //
    // Behaviour is unchanged: [^<>] cannot run past the next "<", so a bare "<"
    // in prose (e.g. "<p>if x < y</p>") is still left alone rather than eating
    // the rest of the line.
    .replace(/<(?=([^<>]*>))\1/g, "");
  // Entity decoding without innerHTML: this text is about to be handed to the
  // user, not re-parsed, and building a DOM node from model output invites an
  // injection vector for no benefit.
  const decoded = doc
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&amp;/gi, "&");
  return decoded
    .replace(/\r\n?/g, "\n")
    .split("\n")
    // trimEnd, not /\s+$/ — the anchored form is the textbook super-linear
    // backtracker on a long run of whitespace (Sonar typescript:S8786).
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Some hosts (notably the Windows Outlook desktop runtime) load the add-in
 * iframe without `clipboard-write` in their Permissions-Policy, which makes
 * `navigator.clipboard.writeText` throw "Clipboard API has been blocked by
 * permissions policy". A pill click is a real user gesture, so the legacy
 * `document.execCommand('copy')` path still works — call this whenever the
 * async API is missing or rejects.
 */
export function execCommandCopyFallback(text: string): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  const previouslyFocused = document.activeElement as HTMLElement | null;
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    // execCommand is deprecated, but it's the only clipboard write available
    // in iframes whose Permissions-Policy omits `clipboard-write`. The async
    // API is preferred by callers and this only runs when it throws. NOSONAR
    return document.execCommand("copy"); // NOSONAR: typescript:S1874
  } catch {
    return false;
  } finally {
    textarea.remove();
    previouslyFocused?.focus?.();
  }
}
